import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';

interface PoseImages {
  /** poseId → override image URL (CloudFront). */
  overrides: Record<string, string>;
  /** Re-fetch the manifest (after an admin approves/reverts an image). */
  refresh: () => void;
}

const PoseImagesContext = createContext<PoseImages>({ overrides: {}, refresh: () => {} });

export function PoseImagesProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    api
      .listPoseImageOverrides()
      .then(setOverrides)
      .catch(() => {
        /* no overrides — bundled art is used */
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PoseImagesContext.Provider value={{ overrides, refresh: load }}>
      {children}
    </PoseImagesContext.Provider>
  );
}

/** The override image URL for a pose, or undefined to use the bundled art. */
export function usePoseImageOverride(poseId: string): string | undefined {
  return useContext(PoseImagesContext).overrides[poseId];
}

/** Re-fetch the override manifest (call after approving/reverting an image). */
export function usePoseImagesRefresh(): () => void {
  return useContext(PoseImagesContext).refresh;
}
